class GameAPIService {
  constructor(gameName, supabaseClient, currentUser) {
    this.gameName = gameName;
    this.supabase = supabaseClient;
    this.currentUser = currentUser;
    this.sessionId = this.getOrCreateSessionId();
    this.baseUrl = `${supabaseClient.supabaseUrl}/functions/v1/game-core`;
    this.supabaseKey = supabaseClient.supabaseKey;
    
    this.gameToken = null;
    this.tokenExpiry = null;
    this.tokenPromise = null;
    
    if (gameName === 'find-the-pumpkins') {
      const savedState = sessionStorage.getItem('pumpkin_game_state');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          if (state.gameToken && state.sessionId) {
            this.gameToken = state.gameToken;
            this.sessionId = state.sessionId;
            this.tokenExpiry = new Date(Date.now() + 7200000).toISOString();
          }
        } catch (error) {
          console.error('[GameAPIService] Error parsing saved state:', error);
        }
      }
    }
    
    // Periodic checks
    this.periodicCheckInterval = null;
    this.nextCheckTime = null;
    this.gameStarted = false;
    this.gameValid = true;
    this.checkCount = 0;
    
    // Config
    this.maxRetries = 3;
    this.retryDelay = 1000;
    
    this.RESPONSE_CODES = {
      SUCCESS: 'OP_200',
      DATA_RECEIVED: 'OP_201',
      SESSION_ACTIVE: 'OP_202',
      INVALID_REQUEST: 'OP_400',
      SESSION_ERROR: 'OP_401',
      RATE_LIMITED: 'OP_429',
      SERVER_ERROR: 'OP_500',
      RELOAD_REQUIRED: 'OP_RELOAD_REQUIRED'
    };
  }

  // ========== Session Management ==========
  
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem(`${this.gameName}_session_id`);
    if (!sessionId) {
      sessionId = this.generateUUID();
      sessionStorage.setItem(`${this.gameName}_session_id`, sessionId);
    }
    return sessionId;
  }

  renewSession() {
    this.sessionId = this.generateUUID();
    sessionStorage.setItem(`${this.gameName}_session_id`, this.sessionId);
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  forceSessionRenewal() {
    this.sessionId = this.generateUUID();
    sessionStorage.setItem(`${this.gameName}_session_id`, this.sessionId);
    this.gameStarted = false;
    this.gameValid = true;
    this.checkCount = 0;
    this.gameToken = null;
    this.tokenExpiry = null;
  }

  // ========== Token Management ==========

  async ensureValidToken() {
    if (this.gameToken && this.tokenExpiry && new Date() < new Date(this.tokenExpiry)) {
      return this.gameToken;
    }

    if (this.tokenPromise) {
      return await this.tokenPromise;
    }

    this.tokenPromise = this.generateNewToken();
    
    try {
      const token = await this.tokenPromise;
      this.tokenPromise = null;
      return token;
    } catch (error) {
      this.tokenPromise = null;
      throw error;
    }
  }

  async generateNewToken() {
    try {
      const response = await fetch(`${this.baseUrl}/generate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.supabaseKey,
          'Authorization': `Bearer ${this.supabaseKey}`,
          'x-session-id': this.sessionId,
          'x-customer-name': this.getCustomerName(),
          'x-game-name': this.gameName
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to generate token: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.code === this.RESPONSE_CODES.SUCCESS) {
        this.gameToken = data.token;
        this.tokenExpiry = data.expires_at;
        
        if (data.session_id && data.session_id !== this.sessionId) {
          this.sessionId = data.session_id;
          sessionStorage.setItem(`${this.gameName}_session_id`, this.sessionId);
        }
      }
      
      return this.gameToken;
    } catch (error) {
      this.log('Token generation failed:', error);
      throw error;
    }
  }

  async getHeaders() {
    const token = await this.ensureValidToken();
    return {
      'Content-Type': 'application/json',
      'apikey': this.supabaseKey,
      'Authorization': `Bearer ${this.supabaseKey}`,
      'x-game-token': token,
      'x-session-id': this.sessionId,
      'x-customer-name': this.getCustomerName(),
      'x-customer-id': this.currentUser?.customerId || null,
      'x-customer-email': this.currentUser?.email || null,
      'x-game-name': this.gameName
    };
  }
  restoreSession(gameToken, sessionId) {
    this.gameToken = gameToken;
    this.sessionId = sessionId;
    console.log('[GameAPIService] Session restored:', { gameToken, sessionId });
  }

  // ========== Game Session Lifecycle ==========

  async startGameSession() {
    try {
      const token = await this.ensureValidToken();
      if (!token) {
        this.log('Failed to get valid token');
        return false;
      }

      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/start-game-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          startTime: Date.now()
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        this.log('Start game session failed:', { status: response.status, error: errorText });
        
        if (response.status === 400 && errorText.includes('already exists')) {
          this.log('Session already exists, generating new session...');
          this.forceSessionRenewal();
          
          const newHeaders = await this.getHeaders();
          const retryResponse = await fetch(`${this.baseUrl}/start-game-session`, {
            method: 'POST',
            headers: newHeaders,
            body: JSON.stringify({
              startTime: Date.now()
            })
          });
          
          if (!retryResponse.ok) {
            this.log('Retry also failed:', retryResponse.status);
            return false;
          }
          
          const retryData = await retryResponse.json();
          
          if (retryData.code === this.RESPONSE_CODES.SUCCESS) {
            this.gameStarted = true;
            this.gameValid = true;
            this.nextCheckTime = retryData.nextCheckTime || (Date.now() + 30000);
            
            setTimeout(() => {
              this.startPeriodicChecks();
            }, 3000);
            
            return true;
          }
        }
        
        return false;
      }

      const data = await response.json();
    
      if (data.code === this.RESPONSE_CODES.SUCCESS) {
        this.gameStarted = true;
        this.gameValid = true;
        this.nextCheckTime = data.nextCheckTime || (Date.now() + 30000);
        setTimeout(() => {
          this.startPeriodicChecks();
        }, 3000);
        
        return true;
      }
      
      this.log('Unexpected response code:', data.code);
      return false;
    } catch (error) {
      this.log('Failed to start game session:', error);
      return false;
    }
  }

  // ========== Periodic Checks ==========

  startPeriodicChecks() {
    if (this.periodicCheckInterval) {
      clearInterval(this.periodicCheckInterval);
    }
    this.periodicCheckInterval = setInterval(() => {
      this.performPeriodicCheck();
    }, 30000);
  }

  async performPeriodicCheck() {
    if (!this.gameStarted || !this.gameValid) {
      return;
    }

    let retryCount = 0;
    
    while (retryCount < this.maxRetries) {
      try {
        const currentGameState = this.getCurrentGameState();
        
        const checkData = {
          ...currentGameState,
          clientDateTime: new Date().toISOString(),
          checkNumber: ++this.checkCount
        };

        const headers = await this.getHeaders();
        
        const response = await fetch(`${this.baseUrl}/periodic-check`, {
          method: 'POST',
          headers,
          body: JSON.stringify(checkData)
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.nextCheckTime) {
            this.nextCheckTime = data.nextCheckTime;
          }
          
          if (data.code === this.RESPONSE_CODES.SESSION_ERROR) {
            this.log('Session error received - stopping checks');
            this.gameValid = false;
            this.stopPeriodicChecks();
          }
          
          return;
          
        } else {
          const errorText = await response.text();
          this.log(`Periodic check failed:`, { status: response.status, error: errorText });
          
          if (response.status >= 500 && retryCount < this.maxRetries - 1) {
            retryCount++;
            const delay = this.retryDelay * Math.pow(2, retryCount - 1);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          } else {
            this.gameValid = false;
            break;
          }
        }
        
      } catch (error) {
        if (retryCount < this.maxRetries - 1) {
          retryCount++;
          const delay = this.retryDelay * Math.pow(2, retryCount - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        } else {
          this.gameValid = false;
          break;
        }
      }
    }
    
    if (!this.gameValid) {
      this.log('Max retries reached or permanent error - marking game invalid');
      this.stopPeriodicChecks();
    }
  }

  stopPeriodicChecks() {
    if (this.periodicCheckInterval) {
      clearInterval(this.periodicCheckInterval);
      this.periodicCheckInterval = null;
    }
    this.gameStarted = false;
  }

  // ========== Score Submission ==========

  async submitScore(gameData) {
    try {
      this.stopPeriodicChecks();
      
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/submit-score`, {
        method: 'POST',
        headers,
        body: JSON.stringify(gameData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.log('Submit score failed:', { status: response.status, error: errorText });
        return { success: false };
      }

      const result = await response.json();
      
      if (result.session_id) {
        this.sessionId = result.session_id;
        sessionStorage.setItem(`${this.gameName}_session_id`, this.sessionId);
      }
      
      return { success: true, data: result };
    } catch (error) {
      this.log('Error submitting score:', error);
      return { success: false, error };
    }
  }

  // ========== Leaderboard ==========

  async getLeaderboard(limit = 10) {
    try {
      
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/leaderboard?limit=${limit}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        this.log('Failed to load leaderboard:', response.status);
        return [];
      }

      const result = await response.json();
      
      if (result.code === this.RESPONSE_CODES.SUCCESS) {
        return result.data || [];
      }
      
      return [];
    } catch (error) {
      this.log('Error loading leaderboard:', error);
      return [];
    }
  }

  async getPersonalBest() {
    try {
      
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/personal-best`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        this.log('Failed to load personal score:', response.status);
        return 0;
      }

      const data = await response.json();
      
      if (data.code === this.RESPONSE_CODES.SUCCESS) {
        return data.high_score || 0;
      }
      
      return 0;
    } catch (error) {
      this.log('Error loading personal score:', error);
      return 0;
    }
  }

  async getGlobalRecord() {
    try {
      
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/global-record`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        this.log('Failed to load global record:', response.status);
        return { score: 0, displayName: '@UNKNOWN' };
      }

      const data = await response.json();
      
      if (data.code === this.RESPONSE_CODES.SUCCESS) {
        return {
          score: data.score || 0,
          displayName: data.displayName || '@UNKNOWN'
        };
      }
      
      return { score: 0, displayName: '@UNKNOWN' };
    } catch (error) {
      this.log('Error loading global record:', error);
      return { score: 0, displayName: '@UNKNOWN' };
    }
  }

  // ========== Utility Methods ==========

  getCustomerName() {
    if (!this.currentUser) return 'Anonymous';
    return this.currentUser.fullName || this.currentUser.name || 'Anonymous';
  }

  getCurrentGameState() {
    console.warn('getCurrentGameState() should be overridden in your game');
    return {
      score: 0,
      level: 1,
      timeElapsed: 0
    };
  }

  isSessionValid() {
    return this.gameValid;
  }

  cleanup() {
    this.stopPeriodicChecks();
    this.gameStarted = false;
    this.gameValid = true;
    this.checkCount = 0;
    this.renewSession();
  }

  log(message, data = null) {
    console.log(`[${this.gameName} API] ${message}`);
  }
}

if (typeof window !== 'undefined') {
  window.GameAPIService = GameAPIService;
}